import { MutableRefObject, RefObject } from 'react';

import { StringHelper } from '@bryntum/gantt';
import { BryntumSchedulerPro } from '@bryntum/gantt-react';

import { SchedulerControls } from 'components/common/bryntumScheduler/SchedulerControlsHook';
import {
    BASELINE_THRESHOLD_DAYS,
    COMPARISON_COLORS,
    DAY,
    UI,
    YEAR,
} from 'components/common/bryntumScheduler/constants';
import { ComparisonMode } from 'components/common/bryntumScheduler/types';
import { getSpiColor } from 'components/common/bryntumScheduler/utils/colors';
import { dayDiff } from 'components/common/bryntumScheduler/utils/date';

const STATUS_BAR_LABEL_GAP = UI.LABEL_GAP_PX + 8;

const shouldShowBaseline = (currentDate: Date | null, baselineDate: Date | null) => {
    if (!currentDate || !baselineDate) return false;
    const diffDays = Math.abs(dayDiff(Number(baselineDate), Number(currentDate)));
    return diffDays > BASELINE_THRESHOLD_DAYS;
};

//  threshold based on timeline span
const getTimelineSpanYears = (sch): number => {
    const ta = sch?.timeAxis;
    const start = ta?.startDate ?? sch?.startDate;
    const end = ta?.endDate ?? sch?.endDate;
    if (!start || !end) return 3;
    return Math.max(0.01, (+end - +start) / YEAR);
};

// According to the number of years you get the threshold to show comparison indicator
const getMinVarianceDays = (spanYears: number): number => {
    if (spanYears >= 7) return 50; // ~4 months
    if (spanYears >= 5) return 40; // ~3 months
    if (spanYears >= 3) return 30; // ~1.5 months
    if (spanYears >= 2) return 20; // ~1 month
    if (spanYears >= 1) return 10; // ~2 weeks
    return 7;
};

// Pixel gate prevents “invisible” connectors that still clutter DOM/layout
const getMinVariancePx = (spanYears: number): number => {
    if (spanYears >= 7) return 12;
    if (spanYears >= 5) return 10;
    if (spanYears >= 3) return 8;
    if (spanYears >= 2) return 6;
    return 4;
};

// Use BOTH gates: days (semantic) + pixels (visual)
const shouldShowCompareVisual = (sch, a, b) => {
    if (!a || !b) return false;

    const spanYears = getTimelineSpanYears(sch);
    const minDays = getMinVarianceDays(spanYears);
    const minPx = getMinVariancePx(spanYears);

    const diffBetweenDays = Math.abs((+a - +b) / DAY);
    if (diffBetweenDays < minDays) return false;

    const ax = sch.getCoordinateFromDate(a);
    const bx = sch.getCoordinateFromDate(b);
    if (ax === -1 || bx === -1) return false;

    return Math.abs(ax - bx) >= minPx;
};

const getMilestoneCompareDate = (eventRecord, mode: ComparisonMode) => {
    if (mode === 'baseline') return eventRecord.baselineStartDate;
    if (mode === 'forecast') return eventRecord.forecastFinishDate; // not supported yet by BE
    return null;
};

const getBarCompareSpan = (eventRecord, mode: ComparisonMode) => {
    if (mode === 'baseline') {
        return { start: eventRecord.baselineStartDate, end: eventRecord.baselineEndDate };
    }
    // not supported by BE yet
    if (mode === 'forecast') {
        return { start: eventRecord.forecastStartDate, end: eventRecord.forecastFinishDate };
    }
    return { start: null, end: null };
};

const getComparisonMode = (controlsRef: MutableRefObject<SchedulerControls>): ComparisonMode => {
    return controlsRef.current?.comparisonMode ?? 'none';
};

const getIsShowLabel = (controlsRef: MutableRefObject<SchedulerControls>): boolean => {
    return controlsRef.current?.isShowLabels ?? true;
};

const buildHtmlLabel = (safeLabel: string, leftPx: number, boxW: number, align: string = 'left') => {
    return `<div class="ms-label" style="left:${leftPx}px; width:${boxW}px; text-align:${align};">
        ${safeLabel}
      </div>`;
};

const getSummaryBarHtml = ({
    eventRecord,
    renderData,
    sch,
    comparisonMode,
    safeLabel,
    spiColor,
    controlsRef,
    LABEL_BOX_W,
    LEFT_LABEL_EXTRA,
    LABEL_GAP_PX,
}: {
    eventRecord;
    renderData;
    sch;
    comparisonMode: ComparisonMode;
    safeLabel: string;
    spiColor: string;
    controlsRef: MutableRefObject<SchedulerControls>;
    LABEL_BOX_W: number;
    LEFT_LABEL_EXTRA: number;
    LABEL_GAP_PX: number;
}) => {
    const startDate = eventRecord.startDate;
    const displayEndDate = eventRecord.displayEndDate || eventRecord.endDate || startDate;

    const sx = sch.getCoordinateFromDate(startDate);
    const ex = sch.getCoordinateFromDate(displayEndDate);

    if (sx === -1 || ex === -1) return '';

    const trueBarW = Math.max(0, Math.round(ex - sx));

    if (renderData) {
        renderData.width = trueBarW;
        renderData.style = { ...renderData.style, overflow: 'visible' };
        renderData.cls.remove('spi-red', 'spi-amber', 'spi-green');
        if (controlsRef.current?.colorByMode === 'SPI') {
            renderData.cls.add(`spi-${spiColor}`); // 'spi-red', 'spi-green', 'spi-amber'
        }
    }

    const labelOnLeft = comparisonMode === 'forecast';
    const labelLeft = labelOnLeft ? -(LABEL_BOX_W + LEFT_LABEL_EXTRA) : trueBarW + LABEL_GAP_PX;

    let compareHtml = '';

    if (comparisonMode === 'baseline' || comparisonMode === 'forecast') {
        const { start: cs, end: ce } = getBarCompareSpan(eventRecord, comparisonMode);

        // Keep your existing “semantic” gate if you still want it,
        // but ALSO require the comparison to be visually meaningful at current zoom.

        const okSemantic = cs && ce && (shouldShowBaseline(startDate, cs) || shouldShowBaseline(displayEndDate, ce));

        const okVisual =
            cs &&
            ce &&
            (shouldShowCompareVisual(sch, startDate, cs) || shouldShowCompareVisual(sch, displayEndDate, ce));

        if (okSemantic && okVisual) {
            const csx = sch.getCoordinateFromDate(cs);
            const cex = sch.getCoordinateFromDate(ce);

            if (csx !== -1 && cex !== -1) {
                const compareLeft = comparisonMode === 'baseline' ? Math.round(csx - sx) : 0;

                const compareW = Math.max(0, Math.round(cex - csx));

                if (compareW >= 4) {
                    const color = COMPARISON_COLORS[comparisonMode] || '#B8BDC2';
                    compareHtml = `<div class="poap-bar-baseline poap-bar-baseline--${comparisonMode}"
                                                style="left:${compareLeft}px; width:${compareW}px; background:${color};">
                                            </div>`;
                }
            }
        }
    }

    return `<div class="ms-wrap">
                   ${compareHtml}
                   ${buildHtmlLabel(safeLabel, labelLeft, LABEL_BOX_W, labelOnLeft ? 'right' : 'left')}
                </div>`;
};

const getMilestoneHtml = ({
    eventRecord,
    renderData,
    sch,
    comparisonMode,
    safeLabel,
    spiColor,
    controlsRef,
    LABEL_BOX_W,
    LEFT_LABEL_EXTRA,
    LABEL_GAP_PX,
    DIAMOND_PX,
}: {
    eventRecord;
    renderData;
    sch;
    comparisonMode: ComparisonMode;
    safeLabel: string;
    spiColor: string;
    controlsRef: MutableRefObject<SchedulerControls>;
    LABEL_BOX_W: number;
    LEFT_LABEL_EXTRA: number;
    LABEL_GAP_PX: number;
    DIAMOND_PX: number;
}) => {
    // ----------------------------
    // Milestones
    // ----------------------------
    const compareDate = getMilestoneCompareDate(eventRecord, comparisonMode);
    if (controlsRef.current?.colorByMode === 'SPI') {
        renderData.eventColor = spiColor;
    }
    const rightBoxLeft = Math.round(DIAMOND_PX / 2 + LABEL_GAP_PX);
    const leftBoxLeft = -(rightBoxLeft + LABEL_BOX_W + LEFT_LABEL_EXTRA);

    const renderMilestoneLabel = (placeLeft) => {
        const left = placeLeft ? leftBoxLeft : rightBoxLeft;
        const align = placeLeft ? 'right' : 'left';
        return buildHtmlLabel(safeLabel, left, LABEL_BOX_W, align);
    };

    // NEW: dynamic gating (days + pixels) for milestone connector/marker
    const okCompare =
        comparisonMode !== 'none' &&
        !!compareDate &&
        shouldShowBaseline(eventRecord.startDate, compareDate) &&
        shouldShowCompareVisual(sch, eventRecord.startDate, compareDate);
    const preferLeftLabel = comparisonMode === 'forecast';

    if (!okCompare) {
        return `<div class="ms-wrap">${renderMilestoneLabel(preferLeftLabel)}</div>`;
    }

    const compareX = sch.getCoordinateFromDate(compareDate);
    const currentX = sch.getCoordinateFromDate(eventRecord.startDate);

    if (compareX === -1 || currentX === -1) {
        return `<div class="ms-wrap">${renderMilestoneLabel(false)}</div>`;
    }

    const dx = Math.round(currentX - compareX);
    // dx is the horizontal coordinate delta between the actual milestone and comparison date;
    // if there is no delta (they align), skip drawing a connector and just render the label.
    if (!dx) return `<div class="ms-wrap">${renderMilestoneLabel(false)}</div>`;

    const markerLeftPx = -dx;

    let connectorLeft = 0;
    let connectorWidth = 0;

    if (dx > 0) {
        const start = -Math.round(DIAMOND_PX * 2 - 22);
        const end = markerLeftPx;
        connectorLeft = end;
        connectorWidth = Math.max(0, start - end);
    } else {
        const start = -Math.round(DIAMOND_PX / 2 - 2);
        const end = markerLeftPx;
        connectorLeft = start;
        connectorWidth = Math.max(0, end - start);
    }
    const isActualPrecedingBaseline = dx < 0 && comparisonMode === 'baseline';
    const labelOnLeft = isActualPrecedingBaseline ? true : preferLeftLabel;
    const color = COMPARISON_COLORS[comparisonMode];

    return `
      <div class="ms-wrap">
        <div class="ms-connector" style="left:${connectorLeft}px; width:${connectorWidth}px; background:${color};"></div>
        <div class="ms-baseline" style="left:${markerLeftPx}px; background:${color};"></div>
        ${renderMilestoneLabel(labelOnLeft)}
      </div>
    `;
};

export function makeEventRenderer({
    controlsRef,
    schedulerInstanceRef,
    StringHelper,
}: {
    schedulerInstanceRef: RefObject<BryntumSchedulerPro>;
    controlsRef: MutableRefObject<SchedulerControls>;
    StringHelper: StringHelper;
}) {
    const { DIAMOND_PX, LABEL_BOX_W, LABEL_GAP_PX, LEFT_LABEL_EXTRA } = UI;

    return function eventRenderer(detail) {
        const { eventRecord, renderData } = detail;
        const sch = schedulerInstanceRef.current?.instance ?? this;

        const showLabel: boolean = getIsShowLabel(controlsRef);
        const comparisonMode = getComparisonMode(controlsRef);
        const spiColor = getSpiColor(eventRecord.originalData.spi);
        const safeLabel = showLabel
            ? StringHelper
                ? StringHelper.encodeHtml(eventRecord.name || '')
                : eventRecord.name || ''
            : '';

        // ----------------------------
        // Bars summary
        // ----------------------------
        if (!eventRecord.isMilestone) {
            return getSummaryBarHtml({
                eventRecord,
                renderData,
                sch,
                safeLabel,
                spiColor,
                controlsRef,
                LABEL_BOX_W,
                LABEL_GAP_PX: STATUS_BAR_LABEL_GAP,
                LEFT_LABEL_EXTRA,
                comparisonMode,
            });
        } else {
            // ----------------------------
            // Milestones
            // ----------------------------
            return getMilestoneHtml({
                eventRecord,
                renderData,
                sch,
                safeLabel,
                spiColor,
                controlsRef,
                LABEL_BOX_W,
                LABEL_GAP_PX,
                LEFT_LABEL_EXTRA,
                comparisonMode,
                DIAMOND_PX,
            });
        }
    };
}
